/**
 * surfaceData.ts -- read the columnar payload into snapshots.
 *
 * TypeScript twin of `backtest.snapshots.snapshots_from_web_payload`. The two
 * must agree, and the parity fixture is generated by running the Python engine
 * over the Python reader's output, so any disagreement here shows up as a parity
 * failure rather than as a quietly different backtest.
 *
 * The payload is produced by `utils/bake_web_data.py`. Wire format (schema 3):
 *
 *   {
 *     schema: 3,
 *     lam: 0.03,
 *     dates: ["2017-01-03", ...],                       // shared date index
 *     expiry_offsets: { "1M": [31, 31, 30, ...], ... }, // integer DAY offsets
 *     t_quote: { "1M": 0.08333333, ... },               // per LABEL, not bucket
 *     buckets: [{ expiry, tenor, swap_tenor,
 *                 F: [...], annuity: [...], alpha: [...], beta: [...],
 *                 rho: [...], nu: [...], atm_vol: [...] }, ...],
 *     rough: { "10Y": { H: [...], rho: [...], eta: [...] }, ... }
 *   }
 *
 * Every column is aligned to `dates`; `null` means that pillar was not quoted on
 * that date. ~1.8 MB raw, ~0.56 MB gzipped for 1,145 dates x 24 buckets.
 *
 * Expiry dates arrive as day offsets rather than strings so the client needs no
 * TARGET holiday calendar, and so `T` is exact integer arithmetic.
 */

import {
  dayNumFromISO,
  type MarketSnapshot,
  makeSnapshot,
  type SmileBucket,
  yearFraction,
} from "./core";

export interface WebPayload {
  schema: number;
  lam?: number;
  dates: string[];
  expiry_offsets: Record<string, (number | null)[]>;
  t_quote: Record<string, number>;
  buckets: {
    expiry: string;
    tenor: string;
    swap_tenor: number;
    F: (number | null)[];
    annuity: (number | null)[];
    alpha: (number | null)[];
    beta: (number | null)[];
    rho: (number | null)[];
    nu: (number | null)[];
    atm_vol: (number | null)[];
  }[];
  rough?: Record<string, { H: (number | null)[]; rho: (number | null)[]; eta: (number | null)[] }>;
}

export interface LoadOptions {
  volHalfSpreadBp?: number;
  priceHalfSpread?: number;
  fundingRate?: number;
}

export interface LoadedSurface {
  snapshots: MarketSnapshot[];
  dates: string[];
  days: number[];
  expiries: string[];
  tenors: string[];
}

export function loadSurface(
  payload: WebPayload,
  opts: LoadOptions = {},
): LoadedSurface {
  if (payload.schema !== 3) {
    throw new Error(
      `unsupported payload schema ${payload.schema}; expected 3. ` +
        `Re-run: python -m utils.bake_web_data`,
    );
  }
  const volHalfSpread = (opts.volHalfSpreadBp ?? 0.5) * 1e-4;
  const priceHalfSpread = opts.priceHalfSpread ?? 0;
  const fundingRate = opts.fundingRate ?? 0;
  const lam = payload.lam ?? 0.03;

  const days = payload.dates.map(dayNumFromISO);
  const n = days.length;
  const perDate: SmileBucket[][] = Array.from({ length: n }, () => []);

  for (const b of payload.buckets) {
    const offsets = payload.expiry_offsets[b.expiry];
    const tQuote = payload.t_quote[b.expiry];
    const rough = payload.rough?.[b.tenor];
    for (let i = 0; i < n; i++) {
      const F = b.F[i];
      const off = offsets?.[i];
      if (F === null || F === undefined || off === null || off === undefined) continue;
      const expiryDay = days[i] + off;
      const T = yearFraction(days[i], expiryDay);
      if (T <= 0) continue;

      const alpha = b.alpha[i];
      const sabr =
        alpha === null || alpha === undefined
          ? null
          : {
              alpha,
              beta: b.beta[i] as number,
              rho: b.rho[i] as number,
              nu: b.nu[i] as number,
              lam,
            };

      const atm = b.atm_vol[i];
      const H = rough?.H[i];
      const roughParams =
        rough && atm !== null && atm !== undefined && H !== null && H !== undefined
          ? {
              H,
              rho: rough.rho[i] as number,
              eta: rough.eta[i] as number,
              atmVol: atm,
            }
          : null;

      if (!sabr && !roughParams) continue;

      perDate[i].push({
        expiry: b.expiry,
        tenor: b.tenor,
        T,
        tQuote: tQuote ?? T,
        expiryDay,
        F,
        annuity: b.annuity[i] as number,
        swapTenor: b.swap_tenor,
        sabr,
        rough: roughParams,
        volHalfSpread,
        priceHalfSpread,
      });
    }
  }

  const snapshots: MarketSnapshot[] = [];
  const dates: string[] = [];
  const outDays: number[] = [];
  for (let i = 0; i < n; i++) {
    if (perDate[i].length === 0) continue;
    snapshots.push(makeSnapshot(days[i], perDate[i], fundingRate));
    dates.push(payload.dates[i]);
    outDays.push(days[i]);
  }
  if (snapshots.length === 0) throw new Error("payload produced no snapshots");

  const expiries = [...new Set(payload.buckets.map((b) => b.expiry))];
  const tenors = [...new Set(payload.buckets.map((b) => b.tenor))];
  return { snapshots, dates, days: outDays, expiries, tenors };
}

/** Inclusive filter on ISO dates. */
export function filterSnapshots(
  snapshots: readonly MarketSnapshot[],
  startISO?: string,
  endISO?: string,
): MarketSnapshot[] {
  const lo = startISO ? dayNumFromISO(startISO) : -Infinity;
  const hi = endISO ? dayNumFromISO(endISO) : Infinity;
  const out = snapshots.filter((s) => s.day >= lo && s.day <= hi);
  if (out.length === 0) {
    throw new Error(`no snapshots between ${startISO ?? "-inf"} and ${endISO ?? "+inf"}`);
  }
  return out;
}
